// --- Core ---
import {FormattedMessage} from "react-intl";
// --- Material UI ---
import {Box, Divider, Grid, Link, Paper, Stack, Typography, useTheme} from "@mui/material";
// --- SaniQ ---
import config from "config";
import {useAppDispatch} from "store";
import Package from "../../../../package.json";
import {company, qurasoftCity, qurasoftStreet} from "src/definitions/qurasoft";
import lightLogo from 'images/logo/praxis-light.svg';
import darkLogo from 'images/logo/praxis-dark.svg';
import Locale from "src/modules/navigation/components/Locale";
import {switchLocale} from "src/modules/app/reducers/App";

const FooterHeading = ({children}) => (
  <Typography variant={"h5"} sx={{mb: 2}}>
    {children}
  </Typography>
);

const Footer = ({...rest}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Paper
      variant={"outlined"}
      elevation={0}
      style={
        {
          borderRadius: 0
        }
      }
      {...rest}
    >
      <Grid
        container
        spacing={2}
        sx={{justifyContent: "space-between", alignItems: "center"}}
      >
        <Box sx={{display: {sm: 'none', md: 'block'}, m: 4}}/>
        <Grid item xs={12} md={3}>
          <Stack sx={{justifyContent: "center", alignItems: "flex-start"}}>
            <Box sx={{mb: 2}}>
              <img src={isDarkMode ? darkLogo : lightLogo} width={80} alt={"SaniQ"}/>
            </Box>
            <Link href="https://qurasoft.de/zweckbestimmung/" target={"_blank"} variant={"body1"}>
              <FormattedMessage id="footer.about.intended_use" defaultMessage="Intended Use"/>
            </Link>
            <Typography variant={"body1"}>
              <FormattedMessage
                id="footer.about.ce_marked"
                defaultMessage="CE-certified medical product."
              />
            </Typography>
            {
              config.appEnv !== 'dist' &&
              <Typography variant={"body1"}>
                Aktuelle Umgebung: {config.appEnv}
              </Typography>
            }
            <Typography variant={"body1"}>
              <FormattedMessage
                id="footer.version"
                defaultMessage="SaniQ Praxis version {version}"
                values={{version: Package.version}}
              />
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={3}>
          <Stack sx={{justifyContent: "center", alignItems: "flex-start"}}>
            <FooterHeading>
              <FormattedMessage id="footer.contact" defaultMessage="Contact"/>
            </FooterHeading>
            <Typography variant={"body1"} color={"textPrimary"}>
              {company}
            </Typography>
            <Typography variant={"body1"} color={"textPrimary"}>
              {qurasoftStreet}
            </Typography>
            <Typography variant={"body1"} color={"textPrimary"}>
              {qurasoftCity}
            </Typography>
            <Link href="mailto:kontakt@qurasoft.de" variant={"body1"}>
              kontakt@qurasoft.de
            </Link>
          </Stack>
        </Grid>
        <Grid item xs={12} md={3}>
          <Stack sx={{justifyContent: "center", alignItems: "flex-start"}}>
            <FooterHeading>
              <FormattedMessage id="footer.legals" defaultMessage="Legals"/>
            </FooterHeading>
            <Link href="https://qurasoft.de/impressum/" target={"_blank"} variant={"body1"}>
              <FormattedMessage id="footer.legal" defaultMessage="Legal notice"/>
            </Link>
            <Link href="https://qurasoft.de/datenschutz/" target={"_blank"} variant={"body1"}>
              <FormattedMessage id="footer.privacy_policy" defaultMessage="Privacy policy"/>
            </Link>
            <Link href="https://cdn.saniq.org/saniq/legals/praxis/agb.pdf" target={"_blank"} variant={"body1"}>
              <FormattedMessage id="footer.tos" defaultMessage="Terms of Service"/>
            </Link>
            <Link href="https://qurasoft.de/downloads" target={"_blank"} variant={"body1"}>
              <FormattedMessage id="footer.downloads" defaultMessage="Downloads"/>
            </Link>
          </Stack>
        </Grid>
      </Grid>
      <Divider sx={{mt: 4, mb: 2}}/>
      <Grid container sx={{justifyContent: "space-between", alignItems: "center"}}>
        <Grid item>
          <Stack
            direction="row"
            spacing={4}
            sx={
              {
                alignItems: "center",
                ml: 4
              }
            }
          >
            <Typography variant={"body1"}>
              ©2025 Qurasoft. Alle Rechte vorbehalten
            </Typography>
            <Link
              href="https://support.qurasoft.de" target={"_blank"}
              variant={"body1"}
            >
              <FormattedMessage id="footer.support" defaultMessage="Support"/>
            </Link>
            <Link href="https://qurasoft.de/kontakt" target={"_blank"} variant={"body1"}>
              <FormattedMessage id="footer.contact.form" defaultMessage="Contact form"/>
            </Link>
            <Link
              href="https://qurasoft.de/open-source-software-saniq-praxis/" target={"_blank"}
              variant={"body1"}
            >
              <FormattedMessage id="footer.licenses" defaultMessage="Open Source Licenses"/>
            </Link>
          </Stack>
        </Grid>
        <Grid item>
          <Locale
            onLocaleChange={
              (locale) => {
                dispatch(switchLocale(locale));
              }
            }
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export {
  Footer
};
